import { RollupKeyField } from '@/components/project-settings/line-item-rollup/useRollupKeyFields'

/**
 * Fixed, illustrative sample data for the rollup config card's "Live
 * example" section — never real customer data. Covers common ERP
 * dimensions (job, phase, GL account, cost type, cost center, activity,
 * department, equipment) so the preview reads realistically across
 * integrations.
 */
type SampleLine = {
  job: string
  phase: string
  cost_type: string
  gl_account: string
  cost_center: string
  activity: string
  department: string
  equipment: string
  amt: number
  qty: number
  rate: number
  unit: string
}

const SAMPLE_LINES: SampleLine[] = [
  {
    job: 'Turn 204',
    phase: 'Labor',
    cost_type: 'Regular',
    gl_account: '6100',
    cost_center: 'CC-100',
    activity: 'Painting',
    department: 'Maintenance',
    equipment: 'None',
    amt: 120,
    qty: 6,
    rate: 20,
    unit: 'hrs',
  },
  {
    job: 'Turn 204',
    phase: 'Materials',
    cost_type: 'Materials',
    gl_account: '6100',
    cost_center: 'CC-100',
    activity: 'Paint',
    department: 'Maintenance',
    equipment: 'None',
    amt: 85,
    qty: 5,
    rate: 17,
    unit: 'gal',
  },
  {
    job: 'Turn 204',
    phase: 'Labor',
    cost_type: 'Overtime',
    gl_account: '6200',
    cost_center: 'CC-100',
    activity: 'Painting',
    department: 'Maintenance',
    equipment: 'Sprayer',
    amt: 60,
    qty: 3,
    rate: 20,
    unit: 'hrs',
  },
  {
    job: 'Grounds Q3',
    phase: 'Labor',
    cost_type: 'Regular',
    gl_account: '6100',
    cost_center: 'CC-200',
    activity: 'Mowing',
    department: 'Landscaping',
    equipment: 'Mower',
    amt: 210,
    qty: 6,
    rate: 35,
    unit: 'hrs',
  },
  {
    job: 'Turn 205',
    phase: 'Labor',
    cost_type: 'Regular',
    gl_account: '6100',
    cost_center: 'CC-100',
    activity: 'Painting',
    department: 'Maintenance',
    equipment: 'None',
    amt: 95,
    qty: 4.75,
    rate: 20,
    unit: 'hrs',
  },
  {
    job: 'Turn 205',
    phase: 'Labor',
    cost_type: 'Regular',
    gl_account: '6100',
    cost_center: 'CC-100',
    activity: 'Painting',
    department: 'Maintenance',
    equipment: 'None',
    amt: 55,
    qty: 2.75,
    rate: 20,
    unit: 'hrs',
  },
]

type DimensionKey = keyof Omit<SampleLine, 'amt' | 'qty' | 'rate' | 'unit'>

const DIMENSION_KEYWORDS: [RegExp, DimensionKey][] = [
  [/\bgl\b|general ledger/, 'gl_account'],
  [/cost.*type/, 'cost_type'],
  [/cost.*cent/, 'cost_center'],
  [/\bjob\b/, 'job'],
  [/\bphase\b/, 'phase'],
  [/activ/, 'activity'],
  [/depart/, 'department'],
  [/equip/, 'equipment'],
]

/**
 * Matches a real, project-specific key field to one of the sample dataset's
 * canonical dimensions by keyword against its label — the sample data can't
 * know a project's actual field names ahead of time. Fields with an
 * unrecognized label (a custom or uncommon integration field) still toggle
 * and render correctly; they just fall back to a generic two-value
 * illustrative dimension instead of a realistic one.
 */
function matchDimension(label: string): DimensionKey | null {
  const lower = label.toLowerCase()
  const match = DIMENSION_KEYWORDS.find(([pattern]) => pattern.test(lower))
  return match ? match[1] : null
}

function fallbackValue(lineIndex: number): string {
  return lineIndex % 2 === 0 ? 'Group A' : 'Group B'
}

function valueFor(
  line: SampleLine,
  lineIndex: number,
  field: RollupKeyField,
): string {
  const dimension = matchDimension(field.label)
  return dimension ? line[dimension] : fallbackValue(lineIndex)
}

function fmtCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function fmtQtyRate(qty: number, unit: string, rate: number): string {
  const qtyText = qty % 1 ? qty.toFixed(2) : String(qty)
  return `${qtyText} ${unit} × ${fmtCurrency(rate)}`
}

export type RollupPreviewLine = {
  keptText: string
  restText: string
  amountText: string
  qtyRateText: string
}

export type RollupPreviewGroup = {
  isMerged: boolean
  lines: RollupPreviewLine[]
  mergedKeptText: string
  mergedRestText: string
  mergedAmountText: string
  /** Null once the group has merged — quantity/rate are nulled on any
   * merged line, regardless of which fields are in the key. */
  mergedQtyRateText: string | null
}

export type RollupPreview = {
  beforeCount: number
  afterCount: number
  groups: RollupPreviewGroup[]
}

/**
 * Computes the client-side "before/after" preview for a rollup config's
 * current grouping key, against the fixed sample dataset above.
 *
 * Survivor rule: a merged group's non-key values come from whichever member
 * line has the largest `amt` ("largest single line"). This preview is
 * illustrative only and not a byte-for-byte match of the export engine.
 */
export function computeRollupPreview(
  keyFields: RollupKeyField[],
  keptFieldIds: Set<string>,
): RollupPreview {
  const keptFields = keyFields.filter((field) =>
    keptFieldIds.has(field.infoTypeId),
  )
  const restFields = keyFields.filter(
    (field) => !keptFieldIds.has(field.infoTypeId),
  )

  const groupKeyFor = (lineIndex: number) =>
    keptFields
      .map((field) => valueFor(SAMPLE_LINES[lineIndex], lineIndex, field))
      .join('|')

  const indexGroups = new Map<string, number[]>()
  SAMPLE_LINES.forEach((_line, lineIndex) => {
    const key = groupKeyFor(lineIndex)
    const group = indexGroups.get(key) ?? []
    group.push(lineIndex)
    indexGroups.set(key, group)
  })

  const textFor = (fields: RollupKeyField[], lineIndex: number): string =>
    fields
      .map((field) => valueFor(SAMPLE_LINES[lineIndex], lineIndex, field))
      .join(' · ')

  const groups: RollupPreviewGroup[] = [...indexGroups.values()].map(
    (lineIndexes) => {
      const isMerged = lineIndexes.length > 1

      const surviving = lineIndexes.reduce((bestIndex, candidateIndex) =>
        SAMPLE_LINES[candidateIndex].amt > SAMPLE_LINES[bestIndex].amt
          ? candidateIndex
          : bestIndex,
      )
      const totalAmt = lineIndexes.reduce(
        (sum, lineIndex) => sum + SAMPLE_LINES[lineIndex].amt,
        0,
      )
      const survivorLine = SAMPLE_LINES[surviving]

      return {
        isMerged,
        lines: lineIndexes.map((lineIndex) => ({
          keptText: textFor(keptFields, lineIndex),
          restText: textFor(restFields, lineIndex),
          amountText: fmtCurrency(SAMPLE_LINES[lineIndex].amt),
          qtyRateText: fmtQtyRate(
            SAMPLE_LINES[lineIndex].qty,
            SAMPLE_LINES[lineIndex].unit,
            SAMPLE_LINES[lineIndex].rate,
          ),
        })),
        mergedKeptText: textFor(keptFields, surviving),
        mergedRestText: textFor(restFields, surviving),
        mergedAmountText: fmtCurrency(totalAmt),
        mergedQtyRateText: isMerged
          ? null
          : fmtQtyRate(survivorLine.qty, survivorLine.unit, survivorLine.rate),
      }
    },
  )

  return {
    beforeCount: SAMPLE_LINES.length,
    afterCount: groups.length,
    groups,
  }
}
